import React, {useState, lazy, Suspense} from 'react';
import { useFormContext } from '../Provider';
import useValidators from '../Validators/useValidator';
import CommonFieldWrapper from './CommonFieldWrapper';
import { mapboxKey } from '../../../Services/Keys';
import Icon from '../../Icons/Icon';
import { useBusinessCoordinates } from '../../../Data/Business';
import Loading from '../../Loading';

const AddressAutofill = lazy(
    () => import(/* webpackPreload: true, webpackChunkName: "mapbox" */'@mapbox/search-js-react').then(module => ({ default: module.AddressAutofill }))
);

const fallbackValue = {};

const Address = ({label, name, help, onChange, type, placeholder, disabled, validators}) => {
    const { formData, updateField, updateValidity, isValidating, isSubmitting, formDisabled } = useFormContext();
    let value = formData[name] ?? fallbackValue;

    const {isValid, error} = useValidators(value, validators, name, updateValidity);
    const {coordinates} = useBusinessCoordinates();
    const [showFullAddress, setShowFullAddress] = useState(!!value?.streetName);

    const handleChange = (address) => {
        updateField(name, address);
        onChange && onChange(address);
    }

    const fillAddress = (result) => {
        const tmp = result?.features?.[0]?.properties;
        if (!tmp) {
            return;
        }

        handleChange({
            flatNumber: null,
            streetNumber: tmp.address_number,
            streetName: tmp.street,
            postcode: tmp.postcode,
            town: tmp.place,
            country: tmp.region,
            custom: null,
        });

        setShowFullAddress(true);
    }

    const isDisabled = formDisabled || disabled || isSubmitting;

    return <CommonFieldWrapper {...{label, name, help, value, isValid, isValidating, error, validators, isDisabled}}>
        <Suspense fallback={<Loading />}>
            <AddressAutofill
                accessToken={mapboxKey}
                onRetrieve={fillAddress}
                options={{
                    language: 'en',
                    country: 'GB',
                    proximity: coordinates ? [coordinates.longitude, coordinates.latitude] : null
                }}
            >
                <div className="relative flex flex-row">
                    <div className="pointer-events-none inset-y-0 left-0 flex items-center pr-1">
                        <span className="fill-gray-400">
                            <Icon icon="MagnifyingGlass" inline />
                        </span>
                    </div>

                    <input className="block w-full border-0 p-0 text-gray-900 placeholder:text-gray-400 focus:ring-0 sm:text-sm sm:leading-6 disabled:text-gray-400 disabled:bg-gray-100"
                        placeholder={placeholder ?? 'Search for an address'}
                        disabled={isDisabled}
                    />

                    <div className="inset-y-0 right-0 flex items-center pl-1">
                        <button className="fill-gray-400 cursor-pointer"
                            onClick={(e) => { setShowFullAddress(true); e.preventDefault(); }}
                        >
                            {showFullAddress === false && <Icon icon="Pencil" inline />}
                        </button>
                    </div>
                </div>
            </AddressAutofill>
        </Suspense>

        {showFullAddress && <FullAddress value={value} handleChange={handleChange} disabled={isDisabled} />}
    </CommonFieldWrapper>
}

const FullAddress = ({value, handleChange, disabled}) => {
    const changeVal = (key, val) => {
        handleChange({...value, [key]: val, custom: true});
    }

    return <div>
        <fieldset>
            <div className="mt-2 -space-y-px rounded-md bg-white shadow-sm">
                <div className="flex -space-x-px">
                    <div className="w-1/2 min-w-0 flex-1">
                        <label htmlFor="streetNumber" className="sr-only">
                            Street number
                        </label>
                        <input
                            type="text"
                            name="streetNumber"
                            id="streetNumber"
                            className="relative block w-full rounded-none rounded-tl-md border-0 bg-transparent py-1.5 text-gray-900 ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:z-10 focus:ring-2 focus:ring-inset focus:ring-brand sm:text-sm sm:leading-6 disabled:text-gray-400 disabled:bg-gray-100"
                            placeholder="Street number"
                            value={value.streetNumber ?? ''}
                            onChange={e => changeVal('streetNumber', e.target.value)}
                            disabled={disabled}
                        />
                    </div>
                    <div className="min-w-0 flex-1">
                        <label htmlFor="flatNumber" className="sr-only">
                            Flat number
                        </label>
                        <input
                            type="text"
                            name="flatNumber"
                            id="flatNumber"
                            className="relative block w-full rounded-none rounded-tr-md border-0 bg-transparent py-1.5 text-gray-900 ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:z-10 focus:ring-2 focus:ring-inset focus:ring-brand sm:text-sm sm:leading-6 disabled:text-gray-400 disabled:bg-gray-100"
                            placeholder="Flat Number"
                            value={value.flatNumber ?? ''}
                            onChange={e => changeVal('flatNumber', e.target.value)}
                            disabled={disabled}
                        />
                    </div>
                </div>
                <div>
                    <label htmlFor="streetName" className="sr-only">
                        Street Name
                    </label>
                    <input
                        type="text"
                        name="streetName"
                        id="streetName"
                        className="relative block w-full rounded-none border-0 bg-transparent py-1.5 text-gray-900 ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:z-10 focus:ring-2 focus:ring-inset focus:ring-brand sm:text-sm sm:leading-6 disabled:text-gray-400 disabled:bg-gray-100"
                        placeholder="Street name"
                        value={value.streetName ?? ''}
                        onChange={e => changeVal('streetName', e.target.value)}
                        disabled={disabled}
                    />
                </div>
                <div>
                    <label htmlFor="town" className="sr-only">
                        Town
                    </label>
                    <input
                        type="text"
                        name="town"
                        id="town"
                        className="relative block w-full rounded-none border-0 bg-transparent py-1.5 text-gray-900 ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:z-10 focus:ring-2 focus:ring-inset focus:ring-brand sm:text-sm sm:leading-6 disabled:text-gray-400 disabled:bg-gray-100"
                        placeholder="Town"
                        value={value.town ?? ''}
                        onChange={e => changeVal('town', e.target.value)}
                        disabled={disabled}
                    />
                </div>
                <div>
                    <label htmlFor="postcode" className="sr-only">
                        Postcode
                    </label>
                    <input
                        type="text"
                        name="postcode"
                        id="postcode"
                        className="relative block w-full rounded-none border-0 bg-transparent py-1.5 text-gray-900 ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:z-10 focus:ring-2 focus:ring-inset focus:ring-brand sm:text-sm sm:leading-6 disabled:text-gray-400 disabled:bg-gray-100"
                        placeholder="Postcode"
                        value={value.postcode ?? ''}
                        onChange={e => changeVal('postcode', e.target.value)}
                        disabled={disabled}
                    />
                </div>
                <div>
                    <label htmlFor="country" className="sr-only">
                        Country
                    </label>
                    <select
                        name="country"
                        id="country"
                        className="relative block w-full rounded-none rounded-bl-md rounded-br-md border-0 bg-transparent py-1.5 text-gray-900 ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:z-10 focus:ring-2 focus:ring-inset focus:ring-brand sm:text-sm sm:leading-6 disabled:text-gray-400 disabled:bg-gray-100"
                        value={value.country ?? ''}
                        onChange={e => changeVal('country', e.target.value)}
                        disabled={disabled}
                    >
                            <option value="">- Country -</option>
                            <option value="Scotland">Scotland</option>
                            <option value="England">England</option>
                            <option value="Northern Ireland">Northern Ireland</option>
                            <option value="Ireland">Ireland</option>
                            <option value="Wales">Wales</option>
                            <option value="Other">Other</option>
                    </select>
                </div>
            </div>
        </fieldset>
    </div>
}

export default Address;
